import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { WorkGroupQuota } from '@/lib/api';
import {
  formatWorkGroupBytesLabel,
  getWorkGroupQuotaPercent,
  getWorkGroupQuotaThreshold,
  type WorkGroupQuotaThreshold,
} from '@/hooks/use-workgroups';

interface WorkGroupQuotaBadgeProps {
  quota: WorkGroupQuota;
  className?: string;
  compact?: boolean;
}

const THRESHOLD_LABELS: Record<WorkGroupQuotaThreshold, string | null> = {
  normal: null,
  warning: 'Approaching limit',
  critical: 'Almost full',
  exceeded: 'Quota exceeded',
};

const BAR_COLORS: Record<WorkGroupQuotaThreshold, string> = {
  normal: 'bg-primary',
  warning: 'bg-warning',
  critical: 'bg-destructive',
  exceeded: 'bg-destructive',
};

const BADGE_VARIANTS: Record<
  WorkGroupQuotaThreshold,
  'secondary' | 'warning' | 'destructive'
> = {
  normal: 'secondary',
  warning: 'warning',
  critical: 'destructive',
  exceeded: 'destructive',
};

export function WorkGroupQuotaBadge({
  quota,
  className,
  compact = false,
}: WorkGroupQuotaBadgeProps) {
  const percent = getWorkGroupQuotaPercent(quota);
  const threshold = getWorkGroupQuotaThreshold(percent);
  const statusLabel = THRESHOLD_LABELS[threshold];
  const usedLabel = formatWorkGroupBytesLabel(quota.usedBytes);
  const totalLabel = formatWorkGroupBytesLabel(quota.quotaBytes);
  const barPercent = Math.min(percent, 100);

  return (
    <div
      className={cn('space-y-1.5', className)}
      data-testid="wg-quota-indicator"
      aria-label={`Storage ${usedLabel} of ${totalLabel} used, ${percent}%`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={BADGE_VARIANTS[threshold]} className="font-normal">
          {compact ? `${usedLabel} / ${totalLabel}` : `Storage ${usedLabel} / ${totalLabel}`}
        </Badge>
        {statusLabel ? (
          <span
            className={cn(
              'text-xs font-medium',
              threshold === 'warning' ? 'text-warning' : 'text-destructive',
            )}
          >
            {statusLabel}
          </span>
        ) : null}
        {!compact ? (
          <span className="text-xs text-muted-foreground">{percent}% used</span>
        ) : null}
      </div>
      <div className="h-1.5 w-full min-w-[8rem] max-w-xs overflow-hidden rounded-full bg-muted">
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={barPercent}
          className={cn('h-full rounded-full transition-all', BAR_COLORS[threshold])}
          style={{ width: `${barPercent}%` }}
        />
      </div>
    </div>
  );
}
